/**
 * Whether a firm's two colours will be legible, told to the firm **before** it
 * publishes rather than discovered afterwards on its own website.
 *
 * This is the same computation the renderer already does — `site/theme.ts`
 * picks black or white on each colour by WCAG contrast, so a firm that chooses
 * pale yellow gets black text on its buttons without knowing why. What is
 * missing without this file is not the behaviour but the *telling*: a firm
 * choosing a colour with poor contrast in either direction gets a page that
 * works and looks washed out, and nothing anywhere says so.
 *
 * **Duplicated on purpose, and it is four lines.** The renderer lives under
 * `apps/web/site/` — a separate entry, server-side, with no React state and no
 * imports from `src/`. Importing across that boundary to save a luminance
 * formula would drag the SPA bundle into the SSR one or the reverse. WCAG's
 * relative-luminance constants are a published standard and not a decision this
 * repo gets to make differently in two places, which is exactly the case where
 * a copy is safe.
 */

export type ContrastTone = 'success' | 'warning'

interface Rgb {
  r: number
  g: number
  b: number
}

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

export function parseHex(value: string | null | undefined): Rgb | null {
  if (!value || !HEX.test(value.trim())) return null
  const body = value.trim().slice(1)
  const full =
    body.length === 3
      ? body
          .split('')
          .map((c) => c + c)
          .join('')
      : body
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  }
}

/** WCAG relative luminance. */
export function luminance({ r, g, b }: Rgb): number {
  const channel = (v: number) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** The page's paper and its darkest ink — the two the renderer works between
 *  (`site/templates/base.ts`, `site/theme.ts: readableOn`). */
const WHITE: Rgb = { r: 255, g: 255, b: 255 }
const BLACK: Rgb = { r: 17, g: 17, b: 17 }

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = luminance(a)
  const lb = luminance(b)
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

/**
 * The best contrast a **button** in this colour can achieve, given that the
 * renderer picks whichever of black or white reads better on it
 * (`site/theme.ts: readableOn`).
 *
 * This is almost always fine, and that is the point: a firm cannot make its
 * own buttons illegible, because the renderer compensates. It is reported so
 * the firm knows *which* it will get — the thing they cannot predict and are
 * surprised by — and it is **not** what the warning is about.
 */
export function bestContrast(colour: string | null | undefined): number | null {
  const rgb = parseHex(colour)
  if (!rgb) return null
  return Math.max(contrastRatio(rgb, WHITE), contrastRatio(rgb, BLACK))
}

/**
 * The contrast of this colour **as text on the page**, which is where a pale
 * colour actually fails.
 *
 * `--primary` is not only a button fill: the base stylesheet uses it as a text
 * colour on the paper — section headings, links, `.price` — and there the
 * renderer has nothing to compensate with, because the paper is white and the
 * firm's colour is the ink. A firm that picks a pale gold gets buttons that
 * read perfectly and headings that do not, which is exactly the failure a
 * best-of-black-or-white check would call fine.
 *
 * That check was the first version of this file and every colour passed it.
 * Written down because it is the sort of test that looks like coverage and is
 * not: the assertion held for `#f7f3c8`, which is unreadable as a heading.
 */
export function textContrast(colour: string | null | undefined): number | null {
  const rgb = parseHex(colour)
  if (!rgb) return null
  return contrastRatio(rgb, WHITE)
}

/**
 * One sentence about the pair, for the panel.
 *
 * **It never blocks and it rarely warns**, because the renderer now does the
 * work: `site/theme.ts: ink` darkens a colour until it reads as text on the
 * paper, and `readableOn` picks black or white for text *on* the colour. So
 * both directions are handled and a firm cannot make its own site illegible.
 *
 * What this says is therefore *what happened*, not *what is wrong*. A firm
 * that picks a pale gold and finds its headings printed a shade darker is owed
 * the reason — otherwise it looks like the product got the colour wrong. That
 * is the whole job of this sentence.
 *
 * The one warning left is for a colour that is not a colour, which is a typo
 * and not a judgement.
 */
export function contrastNote(
  primary: string | null | undefined,
  accent: string | null | undefined,
): { tone: ContrastTone; message: string } {
  const readable = [
    { name: 'primary', ratio: textContrast(primary) },
    { name: 'accent', ratio: textContrast(accent) },
  ].filter((entry): entry is { name: string; ratio: number } => entry.ratio !== null)

  if (readable.length === 0) {
    return {
      tone: 'warning',
      message: 'A colour must look like #1f3d63 — a hash and six characters.',
    }
  }

  const darkened = readable.filter((entry) => entry.ratio < AA).map((entry) => entry.name)
  const buttons = `Your buttons will use ${isDarkText(primary) ? 'dark' : 'white'} text on them, chosen for readability.`

  if (darkened.length > 0) {
    return {
      tone: 'success',
      message: `Your ${darkened.join(' and ')} ${darkened.length === 1 ? 'colour is' : 'colours are'} light for text on a white page, so headings and small print in ${darkened.length === 1 ? 'it' : 'them'} are printed a shade darker to stay readable. Everything else — buttons, borders, highlights — uses your colour exactly. ${buttons}`,
    }
  }
  return { tone: 'success', message: `Both colours read well anywhere on the page. ${buttons}` }
}

/** WCAG AA for small text. `.role` on a firm's site is small text. */
const AA = 4.5

/**
 * What the service will store: a hash and **six** hex characters
 * (`app/domain/site.py: validate_theme`). `parseHex` is kinder and reads
 * `#abc` too, because a readout should say something about a half-typed
 * colour; Save must not send one the service refuses (Wave 14B).
 */
export function isStorableColour(value: string | null | undefined): boolean {
  return !!value && /^#[0-9a-f]{6}$/i.test(value.trim())
}

/**
 * A value an `<input type="color">` accepts — it takes `#rrggbb` only and
 * silently shows black for anything else, which reads as *"my colour became
 * black"*. `#abc` is widened; anything unreadable gives `fallback`.
 */
export function asColourInput(value: string | null | undefined, fallback: string): string {
  const rgb = parseHex(value)
  if (!rgb) return parseHex(fallback) ? asColourInput(fallback, '#000000') : '#000000'
  const hex = (n: number) => n.toString(16).padStart(2, '0')
  return `#${hex(rgb.r)}${hex(rgb.g)}${hex(rgb.b)}`
}

/** The ink the renderer puts on a fill of this colour (`site/theme.ts:
 *  readableOn`) — so a swatch's own label reads the way a button will. */
export function inkOn(colour: string | null | undefined): '#111111' | '#ffffff' {
  return isDarkText(colour) ? '#111111' : '#ffffff'
}

function isDarkText(colour: string | null | undefined): boolean {
  const rgb = parseHex(colour)
  if (!rgb) return false
  return contrastRatio(rgb, BLACK) > contrastRatio(rgb, WHITE)
}
